import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { generatePassword } from '../utils/passwordGenerator';

function CertBodies() {
  const [certBodies, setCertBodies] = useState([]);
  const [accreditationMap, setAccreditationMap] = useState({}); // cert_body_id → accreditation
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingBody, setEditingBody] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBody, setDeletingBody] = useState(null);
  const [showISOModal, setShowISOModal] = useState(false);
  const [isoBody, setIsoBody] = useState(null);
  const [allStandards, setAllStandards] = useState([]);
  const [authorisedIds, setAuthorisedIds] = useState([]);
  const [isoSaving, setIsoSaving] = useState(false);
  const [showClientsModal, setShowClientsModal] = useState(false);
  const [clientsBody, setClientsBody] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingBody, setBillingBody] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [billingSettings, setBillingSettings] = useState({
    billing_required: true,
    superadmin_override: false,
    reason: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    accreditation_number: ''
  });

  useEffect(() => {
    fetchCertBodies();
  }, []);

  const fetchCertBodies = async () => {
    try {
      setLoading(true);
      const [cbRes, accRes] = await Promise.all([
        axiosClient.get('/cert-bodies/'),
        axiosClient.get('/accreditations/').catch(() => ({ data: [] })),
      ]);
      setCertBodies(cbRes.data);

      // Build accreditation map: prefer GRANTED, then most recent
      const map = {};
      const STATUS_ORDER = { approved: 0, suspended: 1, pending: 2, expired: 3, revoked: 4 };
      const ENUM_SIMPLE = {
        granted: 'approved', applied: 'pending', under_review: 'pending',
        document_review: 'pending', initial_assessment: 'pending',
        witness_audit: 'pending', technical_review: 'pending',
        suspended: 'suspended', withdrawn: 'revoked', expired: 'expired', rejected: 'revoked',
      };
      for (const acc of accRes.data) {
        const cbId = acc.cert_body_id;
        const simple = acc.status;
        const existing = map[cbId];
        if (!existing || (STATUS_ORDER[simple] ?? 99) < (STATUS_ORDER[existing.status] ?? 99)) {
          map[cbId] = acc;
        }
      }
      setAccreditationMap(map);
      setError(null);
    } catch (err) {
      console.error('Error fetching cert bodies:', err);
      setError('Failed to load certification bodies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Generate username from company name
      const username = formData.name.replace(/\s+/g, '').toLowerCase() + '_admin';
      const generatedPassword = generatePassword();

      await axiosClient.post('/cert-bodies/', {
        company_name: formData.name,
        user_email: formData.email,
        user_username: username,
        user_password: generatedPassword,
        user_full_name: formData.name + ' Admin',
        contact_email: formData.email,
        contact_phone: formData.phone
      });
      setShowCreateForm(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        accreditation_number: ''
      });
      fetchCertBodies();
      alert(`Certification body created successfully!\n\nLogin credentials:\nUsername: ${username}\nEmail: ${formData.email}\nTemporary Password: ${generatedPassword}\n\nPlease save this password — it cannot be retrieved later.`);
    } catch (err) {
      console.error('Error creating cert body:', err);
      alert('Failed to create certification body: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEdit = (body) => {
    setEditingBody(body);
    setFormData({
      name: body.name || body.company_name || '',
      email: body.contact_email || '',
      phone: body.contact_phone || '',
      address: body.address || '',
      accreditation_number: body.accreditation_number || ''
    });
    setShowEditForm(true);
    setShowCreateForm(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axiosClient.put(`/cert-bodies/${editingBody.id}`, {
        company_name: formData.name,
        contact_email: formData.email,
        contact_phone: formData.phone,
        address: formData.address,
        accreditation_number: formData.accreditation_number
      });
      setShowEditForm(false);
      setEditingBody(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        accreditation_number: ''
      });
      fetchCertBodies();
    } catch (err) {
      console.error('Error updating cert body:', err);
      alert('Failed to update certification body: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingBody(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      accreditation_number: ''
    });
  };

  const handleDeleteClick = (body) => {
    setDeletingBody(body);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingBody) return;

    try {
      await axiosClient.delete(`/cert-bodies/${deletingBody.id}`);
      setShowDeleteModal(false);
      setDeletingBody(null);
      setSuccessMessage('Certification body deleted successfully');
      fetchCertBodies();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting cert body:', err);
      setError(err.response?.data?.detail || 'Failed to delete certification body');
      setShowDeleteModal(false);
      setDeletingBody(null);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingBody(null);
  };

  const toggleCPDPermission = async (userId, canIssue) => {
    try {
      await axiosClient.patch(`/users/${userId}/cpd-permission`, { can_issue_cpd: canIssue });
      setSuccessMessage(`CPD permission ${canIssue ? 'granted' : 'revoked'} successfully`);
      fetchCertBodies();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update CPD permission');
      setTimeout(() => setError(''), 3000);
    }
  };

  const toggleCompanyAssessmentPermission = async (userId, canIssue) => {
    try {
      await axiosClient.patch(`/users/${userId}/company-assessment-permission`, { can_issue_company_assessments: canIssue });
      setSuccessMessage(`Company Assessment permission ${canIssue ? 'granted' : 'revoked'} successfully`);
      fetchCertBodies();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update Company Assessment permission');
      setTimeout(() => setError(''), 3000);
    }
  };

  const toggleISOCertificatePermission = async (userId, canIssue) => {
    try {
      await axiosClient.patch(`/users/${userId}/iso-certificate-permission`, { can_issue_iso_certificates: canIssue });
      setSuccessMessage(`ISO Certificate permission ${canIssue ? 'granted' : 'revoked'} successfully`);
      fetchCertBodies();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update ISO Certificate permission');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleViewClients = async (body) => {
    setClientsBody(body);
    setClientsList([]);
    setClientsLoading(true);
    setShowClientsModal(true);
    try {
      const res = await axiosClient.get(`/cert-bodies/${body.id}`);
      setClientsList(res.data.clients || []);
    } catch {
      setClientsList([]);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleISOClick = async (body) => {
    setIsoBody(body);
    try {
      const res = await axiosClient.get(`/cert-bodies/${body.id}/iso-standards`);
      setAllStandards(res.data.all_standards || []);
      setAuthorisedIds(res.data.authorised_ids || []);
    } catch {
      setAllStandards([]);
      setAuthorisedIds([]);
    }
    setShowISOModal(true);
  };

  const handleISOSave = async () => {
    setIsoSaving(true);
    try {
      await axiosClient.patch(`/cert-bodies/${isoBody.id}/iso-standards`, { iso_standard_ids: authorisedIds });
      setSuccessMessage(`ISO standards updated for ${isoBody.name}`);
      setShowISOModal(false);
    } catch (err) {
      setError('Failed to update ISO standards: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsoSaving(false);
    }
  };

  const toggleISO = (id) => {
    setAuthorisedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBillingClick = async (body) => {
    setBillingBody(body);
    try {
      const response = await axiosClient.get(`/certification-bodies/${body.id}/billing`);
      setBillingData(response.data);
      setBillingSettings({
        billing_required: response.data.billing_required,
        superadmin_override: response.data.superadmin_override ?? response.data.billing_override,
        reason: ''
      });
      setShowBillingModal(true);
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing information');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBillingUpdate = async () => {
    if (!billingBody) return;

    try {
      await axiosClient.patch(`/certification-bodies/${billingBody.id}/billing`, {
        billing_required: billingSettings.billing_required,
        billing_override: billingSettings.superadmin_override,
        reason: billingSettings.reason || undefined
      });
      setSuccessMessage('Billing settings updated successfully');
      setShowBillingModal(false);
      setBillingBody(null);
      setBillingData(null);
      fetchCertBodies();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating billing settings:', err);
      setError(err.response?.data?.detail || 'Failed to update billing settings');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBillingCancel = () => {
    setShowBillingModal(false);
    setBillingBody(null);
    setBillingData(null);
    setBillingSettings({
      billing_required: true,
      superadmin_override: false,
      reason: ''
    });
  };

  const getAccreditationBadge = (status) => {
    const map = {
      approved:  { text: 'Approved',  color: '#155724', bgColor: '#d4edda' },
      pending:   { text: 'Pending',   color: '#555',    bgColor: '#e0e0e0' },
      suspended: { text: 'Suspended', color: '#856404', bgColor: '#fff3cd' },
      revoked:   { text: 'Revoked',   color: '#721c24', bgColor: '#f8d7da' },
      expired:   { text: 'Expired',   color: '#383d41', bgColor: '#d6d8db' },
    };
    return map[status] || { text: 'None', color: '#999', bgColor: '#f0f0f0' };
  };

  const getBillingStatus = (cb) => {
    if (!cb.billing_required) return 'No Billing Required';
    if (cb.superadmin_override || cb.billing_override) return 'Override Active';
    const ps = cb.payment_status || cb.monthly_payment_status || 'unpaid';
    if (ps === 'paid') return 'Paid';
    if (ps === 'overdue') return 'Overdue';
    return 'Billing Required (Unpaid)';
  };

  const getBillingStatusBadge = (body) => {
    const text = getBillingStatus(body);
    const colorMap = {
      'No Billing Required':    { color: '#155724', bgColor: '#d4edda' },
      'Override Active':        { color: '#856404', bgColor: '#fff3cd' },
      'Paid':                   { color: '#155724', bgColor: '#d4edda' },
      'Overdue':                { color: '#721c24', bgColor: '#f8d7da' },
      'Billing Required (Unpaid)': { color: '#856404', bgColor: '#fff3cd' },
    };
    return { text, ...(colorMap[text] || { color: '#333', bgColor: '#eee' }) };
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading certification bodies...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Certification Bodies</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showCreateForm ? 'Cancel' : '+ Create Certification Body'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{ padding: '10px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px', marginBottom: '20px' }}>
          {successMessage}
        </div>
      )}

      {showEditForm && editingBody && (
        <div style={{ backgroundColor: '#fff3cd', padding: '20px', borderRadius: '4px', marginBottom: '20px', border: '2px solid #ffc107' }}>
          <h2>Edit Certification Body</h2>
          <form onSubmit={handleUpdate}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Phone</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Accreditation Number</label>
              <input
                type="text"
                name="accreditation_number"
                value={formData.accreditation_number}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#ffc107',
                  color: '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Update Certification Body
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showCreateForm && (
        <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '4px', marginBottom: '20px' }}>
          <h2>Create New Certification Body</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Phone</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Accreditation Number</label>
              <input
                type="text"
                name="accreditation_number"
                value={formData.accreditation_number}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Create Certification Body
            </button>
          </form>
        </div>
      )}

      <div style={{ backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#343a40', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Admin Users</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Accreditation #</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Accred. Status</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Accred. Expiry</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Clients</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Billing Status</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>CPD</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Company Assessments</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>ISO Certificates</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {certBodies.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  No certification bodies found. Create one to get started.
                </td>
              </tr>
            ) : (
              certBodies.map((body) => {
                // Get the first admin user (primary admin)
                const adminUsers = body.users || body.admin_users || [];
                const primaryAdmin = adminUsers[0];
                const billingBadge = getBillingStatusBadge(body);

                return (
                  <tr key={body.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{body.name || body.company_name}</td>
                    <td style={{ padding: '12px' }}>
                      {adminUsers.length > 0 ? (
                        <div>
                          {adminUsers.map((user, idx) => (
                            <div key={user.id} style={{ marginBottom: idx < adminUsers.length - 1 ? '8px' : '0' }}>
                              <div style={{ fontSize: '13px', fontWeight: '500' }}>{user.username}</div>
                              <div style={{ fontSize: '12px', color: '#6c757d' }}>{user.email}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>{body.accreditation_number || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {(() => {
                        const acc = accreditationMap[body.id];
                        if (!acc) return <span style={{ color: '#999', fontSize: '12px' }}>Not accredited</span>;
                        const badge = getAccreditationBadge(acc.status);
                        return (
                          <span style={{
                            padding: '3px 8px', borderRadius: '4px', fontSize: '12px',
                            fontWeight: '500', backgroundColor: badge.bgColor, color: badge.color,
                          }}>
                            {badge.text}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#555' }}>
                      {accreditationMap[body.id]?.expires_at
                        ? new Date(accreditationMap[body.id].expires_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td style={{ padding: '12px' }}>{body.client_count || 0}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: billingBadge.bgColor,
                        color: billingBadge.color
                      }}>
                        {billingBadge.text}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {primaryAdmin ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {primaryAdmin.can_issue_cpd ? (
                            <span style={{ color: '#28a745', fontSize: '13px' }}>Enabled</span>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '13px' }}>Disabled</span>
                          )}
                          <button
                            onClick={() => toggleCPDPermission(primaryAdmin.id, !primaryAdmin.can_issue_cpd)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: primaryAdmin.can_issue_cpd ? '#dc3545' : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {primaryAdmin.can_issue_cpd ? 'Revoke' : 'Grant'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: '#6c757d', fontSize: '13px' }}>No admin</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {primaryAdmin ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {primaryAdmin.can_issue_company_assessments ? (
                            <span style={{ color: '#28a745', fontSize: '13px' }}>Enabled</span>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '13px' }}>Disabled</span>
                          )}
                          <button
                            onClick={() => toggleCompanyAssessmentPermission(primaryAdmin.id, !primaryAdmin.can_issue_company_assessments)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: primaryAdmin.can_issue_company_assessments ? '#dc3545' : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {primaryAdmin.can_issue_company_assessments ? 'Revoke' : 'Grant'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: '#6c757d', fontSize: '13px' }}>No admin</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {primaryAdmin ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {primaryAdmin.can_issue_iso_certificates ? (
                            <span style={{ color: '#28a745', fontSize: '13px' }}>Enabled</span>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '13px' }}>Disabled</span>
                          )}
                          <button
                            onClick={() => toggleISOCertificatePermission(primaryAdmin.id, !primaryAdmin.can_issue_iso_certificates)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: primaryAdmin.can_issue_iso_certificates ? '#dc3545' : '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {primaryAdmin.can_issue_iso_certificates ? 'Revoke' : 'Grant'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: '#6c757d', fontSize: '13px' }}>No admin</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleViewClients(body)}
                          style={{ padding: '6px 12px', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                        >
                          Clients ({body.client_count || 0})
                        </button>
                        <button
                          onClick={() => handleISOClick(body)}
                          style={{ padding: '6px 12px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                        >
                          ISO Standards
                        </button>
                        <button
                          onClick={() => handleBillingClick(body)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Billing
                        </button>
                        <button
                          onClick={() => handleEdit(body)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#495057',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(body)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingBody && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginTop: 0, color: '#dc3545', fontSize: '24px' }}>Delete Certification Body</h2>
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <strong style={{ color: '#856404', fontSize: '14px' }}>Warning:</strong>
              <p style={{ margin: '10px 0 0 0', color: '#856404', fontSize: '14px' }}>
                This action cannot be undone. This will permanently delete:
              </p>
              <ul style={{ margin: '10px 0 0 20px', color: '#856404', fontSize: '14px', paddingLeft: 0 }}>
                <li>Certification body: <strong>{deletingBody.name || deletingBody.company_name}</strong></li>
                <li>All associated admin users</li>
                <li>All related data</li>
              </ul>
            </div>

            {deletingBody.client_count > 0 && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                padding: '15px',
                marginBottom: '20px',
                color: '#721c24'
              }}>
                <strong>Cannot delete:</strong> This certification body has {deletingBody.client_count} client(s).
                You must remove or reassign all clients before deleting this certification body.
              </div>
            )}

            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              Are you sure you want to delete this certification body?
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deletingBody.client_count > 0}
                style={{
                  padding: '10px 20px',
                  backgroundColor: deletingBody.client_count > 0 ? '#ccc' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: deletingBody.client_count > 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {deletingBody.client_count > 0 ? 'Cannot Delete' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Settings Modal */}
      {showBillingModal && billingBody && billingData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, color: '#28a745', fontSize: '24px' }}>
              Billing Settings - {billingBody.name || billingBody.company_name}
            </h2>

            {/* Current Status */}
            <div style={{
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <h3 style={{ marginTop: 0, fontSize: '16px', color: '#1976d2' }}>Current Status</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                <div>
                  <strong>Payment Status:</strong>
                  <div style={{
                    display: 'inline-block',
                    marginLeft: '8px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: getBillingStatusBadge(billingBody).bgColor,
                    color: getBillingStatusBadge(billingBody).color,
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {billingData.payment_status || billingData.monthly_payment_status || 'unpaid'}
                  </div>
                </div>
                <div>
                  <strong>Last Payment:</strong> {billingData.last_payment_date ? new Date(billingData.last_payment_date).toLocaleDateString() : 'Never'}
                </div>
                <div>
                  <strong>Next Due:</strong>{' '}
                  {(billingData.next_due || billingData.next_payment_due)
                    ? new Date(billingData.next_due || billingData.next_payment_due).toLocaleDateString()
                    : 'N/A'}
                </div>
              </div>
            </div>

            {/* Billing Controls */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0, fontSize: '16px', color: '#333' }}>Billing Controls (SuperAdmin Only)</h3>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <input
                    type="checkbox"
                    checked={billingSettings.billing_required}
                    onChange={(e) => setBillingSettings({ ...billingSettings, billing_required: e.target.checked })}
                    style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>Billing Required</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      If enabled, this certification body must have paid status to operate
                    </div>
                  </div>
                </label>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                  <input
                    type="checkbox"
                    checked={billingSettings.superadmin_override}
                    onChange={(e) => setBillingSettings({ ...billingSettings, superadmin_override: e.target.checked })}
                    style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#856404' }}>SuperAdmin Override</div>
                    <div style={{ fontSize: '12px', color: '#856404' }}>
                      Allow operation without payment (bypasses billing requirements)
                    </div>
                  </div>
                </label>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                  Reason for Change (Optional)
                </label>
                <textarea
                  value={billingSettings.reason}
                  onChange={(e) => setBillingSettings({ ...billingSettings, reason: e.target.value })}
                  placeholder="Enter reason for this change (logged in audit trail)..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Access Rules Explanation */}
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px',
              fontSize: '13px'
            }}>
              <h4 style={{ marginTop: 0, fontSize: '14px', color: '#495057' }}>Access Control Rules</h4>
              <p style={{ margin: '5px 0', color: '#666' }}>
                Access is granted if <strong>ANY</strong> of these conditions are true:
              </p>
              <ul style={{ margin: '5px 0 0 20px', paddingLeft: 0, color: '#666' }}>
                <li>Billing Required is <strong>disabled</strong> → <em>No Billing Required</em></li>
                <li>SuperAdmin Override is <strong>enabled</strong> → <em>Override Active</em></li>
                <li>Payment Status is <strong>paid</strong> → <em>Paid</em></li>
              </ul>
              <p style={{ margin: '10px 0 0 0', color: '#666' }}>
                Otherwise, access is restricted (<em>Overdue</em> or <em>Billing Required (Unpaid)</em>).
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleBillingCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBillingUpdate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Save Billing Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ISO Standards Modal */}
      {showISOModal && isoBody && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '8px', width: '100%', maxWidth: '640px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px' }}>Authorise ISO Standards</h2>
                <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>{isoBody.name}</p>
              </div>
              <button onClick={() => setShowISOModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
                Select which ISO standards this cert body is authorised to certify against. These are granted without requiring payment.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {allStandards.map(s => {
                  const active = authorisedIds.includes(s.id);
                  return (
                    <label key={s.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px', borderRadius: '6px', cursor: 'pointer',
                      border: `1px solid ${active ? '#6f42c1' : '#dee2e6'}`,
                      background: active ? '#f3f0ff' : 'white',
                    }}>
                      <input type="checkbox" checked={active} onChange={() => toggleISO(s.id)} style={{ accentColor: '#6f42c1' }} />
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13px' }}>{s.code}</div>
                        <div style={{ fontSize: '11px', color: '#666' }}>{s.name}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#666' }}>{authorisedIds.length} standard{authorisedIds.length !== 1 ? 's' : ''} selected</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setShowISOModal(false)} style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleISOSave} disabled={isoSaving} style={{ padding: '8px 16px', background: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: isoSaving ? 'not-allowed' : 'pointer' }}>
                  {isoSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Clients Modal ─────────────────────────────────────── */}
      {showClientsModal && clientsBody && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '12px', width: '100%', maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#1a1a1a' }}>
                  Clients — {clientsBody.name}
                </div>
                <div style={{ fontSize: '13px', color: '#666', marginTop: 2 }}>
                  {clientsLoading ? 'Loading…' : `${clientsList.length} client${clientsList.length !== 1 ? 's' : ''}`}
                </div>
              </div>
              <button onClick={() => setShowClientsModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {clientsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Loading clients…</div>
              ) : clientsList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No clients found for this certification body.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #dee2e6', background: '#f8f9fa' }}>
                      {['Company', 'Industry', 'Size', 'Users', 'Status'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {clientsList.map((client, i) => (
                      <tr key={client.id} style={{ borderBottom: i < clientsList.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <td style={{ padding: '10px 12px', fontWeight: '600', color: '#1a1a1a' }}>{client.name}</td>
                        <td style={{ padding: '10px 12px', color: '#555' }}>{client.industry || '—'}</td>
                        <td style={{ padding: '10px 12px', color: '#555' }}>{client.size || '—'}</td>
                        <td style={{ padding: '10px 12px', color: '#555' }}>{client.user_count ?? 0}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500',
                            backgroundColor: client.implementation_status === 'active' ? '#d4edda' : client.implementation_status === 'in_progress' ? '#fff3cd' : '#e2e3e5',
                            color: client.implementation_status === 'active' ? '#155724' : client.implementation_status === 'in_progress' ? '#856404' : '#383d41',
                          }}>
                            {client.implementation_status ? client.implementation_status.replace(/_/g, ' ') : 'Not set'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowClientsModal(false)} style={{ padding: '8px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertBodies;
